import {
	ArrowLeftOutlined,
	DatabaseOutlined,
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { getClientEnv } from "@shared/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Image, Input, InputNumber, Modal, message, Popconfirm, Upload } from "antd";
import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canManageObjectStructure } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { type BlockCreatePayload, blocksApi } from "../api/blocks.api";
import { buildingsApi } from "../api/buildings.api";
import { filesApi } from "../api/files.api";
import type { Block } from "../types/objects.types";
import { getUploadedAssetUrl } from "../utils/uploaded-asset-url";

const env = getClientEnv();

function BlockPreview({
	block,
	onDelete,
}: {
	block: Block;
	onDelete?: (type: "image" | "image3d" | "blockSvg") => void;
}) {
	const activeType = block.image
		? "image"
		: block.image3d
			? "image3d"
			: block.blockSvg
				? "blockSvg"
				: undefined;

	const blockImageUrl = getUploadedAssetUrl(
		env.VITE_API_URL,
		block[activeType as keyof Block] as string | undefined
	);

	return (
		<div
			className="crm-imgph crm-block-preview"
			style={{
				padding: blockImageUrl ? 0 : undefined,
				position: "relative",
			}}
		>
			{blockImageUrl ? (
				<div className="crm-image-preview-wrapper" style={{ width: "100%", height: "100%" }}>
					<Image
						alt={block.name}
						src={blockImageUrl}
						style={{ width: "100%", height: "100%", objectFit: "cover" }}
						wrapperStyle={{ width: "100%", height: "100%" }}
					/>
					{onDelete && activeType && (
						<Button
							type="text"
							icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
							onClick={(e) => {
								e.stopPropagation();
								Modal.confirm({
									title: "Rasmni o'chirish",
									content: `Haqiqatan ham blokning ${
										activeType === "image"
											? "2D planini"
											: activeType === "image3d"
												? "3D planini"
												: "interaktiv SVG planini"
									} o'chirib tashlamoqchimisiz?`,
									okText: "O'chirish",
									okType: "danger",
									cancelText: "Bekor qilish",
									onOk: () => {
										onDelete(activeType);
									},
								});
							}}
							className="crm-image-delete-btn"
						/>
					)}
				</div>
			) : (
				<>
					<DatabaseOutlined style={{ fontSize: "20px", color: "var(--crm-ink-3)", opacity: 0.5 }} />
					<span style={{ fontSize: "9px" }}>2D/3D plan</span>
				</>
			)}
		</div>
	);
}

type BlockUploadFieldProps = {
	accept: string;
	existingAsset?: string;
	file?: File;
	label: string;
	onFileChange: (file: File | undefined) => void;
};

function BlockUploadField({
	accept,
	existingAsset,
	file,
	label,
	onFileChange,
}: BlockUploadFieldProps) {
	const fileName = file?.name || existingAsset?.split("/").pop();
	const hasFile = Boolean(file || existingAsset);

	return (
		<Form.Item label={label}>
			<Upload
				accept={accept}
				beforeUpload={(nextFile) => {
					onFileChange(nextFile);
					return false;
				}}
				maxCount={1}
				onRemove={() => {
					onFileChange(undefined);
				}}
				showUploadList={false}
			>
				<div className="crm-block-upload-card">
					<div className={`crm-block-upload-status ${hasFile ? "ok" : ""}`}>
						<span>{hasFile ? "Fayl qo'shilgan" : "Fayl tanlanmagan"}</span>
					</div>
					<div className="crm-block-upload-meta">
						<span className="crm-block-upload-name">{fileName || "Fayl tanlanmagan"}</span>
						<span className="crm-block-upload-action">
							<UploadOutlined /> Yuklash
						</span>
					</div>
				</div>
			</Upload>
			{file ? (
				<Button
					className="crm-block-upload-remove"
					danger
					icon={<DeleteOutlined />}
					onClick={() => onFileChange(undefined)}
					size="small"
					type="text"
				>
					O'chirish
				</Button>
			) : null}
		</Form.Item>
	);
}

export function BlocksPage() {
	const { buildingId } = useParams();
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const canManageBlocks = canManageObjectStructure(role);
	const [modalVisible, setModalVisible] = useState(false);
	const [form] = Form.useForm<{ floorsCount: number; name: string }>();
	const [plan2dFile, setPlan2dFile] = useState<File>();
	const [plan3dFile, setPlan3dFile] = useState<File>();
	const [svgFile, setSvgFile] = useState<File>();
	const [editingBlock, setEditingBlock] = useState<Block | null>(null);

	const { data: building, isLoading: isBuildingLoading } = useQuery({
		queryKey: ["building", buildingId],
		queryFn: () => buildingsApi.getBuilding(buildingId ?? ""),
		enabled: !!buildingId,
	});

	const roomsMapBaseParams = building?.orgId
		? `orgId=${building.orgId}&buildingId=${building.id}`
		: `buildingId=${buildingId}`;

	const { data: buildingBlocks = [] } = useQuery({
		queryKey: ["blocks", buildingId],
		queryFn: () => blocksApi.getBlocks({ buildingId, limit: 100 }),
		enabled: !!buildingId,
	});

	const createBlockMutation = useMutation({
		mutationFn: blocksApi.createBlock,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["blocks", buildingId] });
			message.success("Blok muvaffaqiyatli qo'shildi!");
			setModalVisible(false);
			resetCreateModal();
		},
		onError: (error: unknown) => {
			message.error(parseApiError(error) || "Blok yaratishda xatolik yuz berdi");
		},
	});

	const updateBlockMutation = useMutation({
		mutationFn: ({ id, payload }: { id: string; payload: Partial<BlockCreatePayload> }) =>
			blocksApi.updateBlock(id, payload),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["blocks", buildingId] });
			message.success("Blok muvaffaqiyatli yangilandi!");
			setModalVisible(false);
			setEditingBlock(null);
			resetCreateModal();
		},
		onError: (error: unknown) => {
			message.error(parseApiError(error) || "Blokni yangilashda xatolik yuz berdi");
		},
	});

	const deleteBlockMutation = useMutation({
		mutationFn: blocksApi.deleteBlock,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["blocks", buildingId] });
			message.success("Blok muvaffaqiyatli o'chirildi!");
		},
		onError: (error: unknown) => {
			message.error(parseApiError(error) || "Blokni o'chirishda xatolik yuz berdi");
		},
	});

	if (isBuildingLoading) {
		return <div className="crm-card pad empty">Yuklanmoqda...</div>;
	}

	if (!building) {
		return (
			<div className="crm-card pad empty">
				Bino topilmadi.
				<Button className="crm-mt" onClick={() => navigate("/objects")}>
					Orqaga
				</Button>
			</div>
		);
	}

	const resetCreateModal = () => {
		form.resetFields();
		setPlan2dFile(undefined);
		setPlan3dFile(undefined);
		setSvgFile(undefined);
		setEditingBlock(null);
	};

	const handleEditBlock = (block: Block) => {
		setEditingBlock(block);
		form.setFieldsValue({ name: block.name, floorsCount: block.floorsCount });
		setModalVisible(true);
	};

	const handleDeleteBlock = (block: Block) => {
		deleteBlockMutation.mutate(block.id);
	};

	const handleSaveBlock = () => {
		if (!canManageBlocks) {
			message.warning("Menejer blok strukturasini o'zgartira olmaydi.");
			return;
		}

		form.validateFields().then(async (values) => {
			try {
				const [plan2d, plan3d, svg] = await Promise.all([
					plan2dFile ? filesApi.uploadFile(plan2dFile, "image") : Promise.resolve(undefined),
					plan3dFile ? filesApi.uploadFile(plan3dFile, "image") : Promise.resolve(undefined),
					svgFile ? filesApi.uploadFile(svgFile, "document") : Promise.resolve(undefined),
				]);

				const payload = {
					name: values.name,
					floorsCount: values.floorsCount,
					...(plan2d?.url ? { image: plan2d.url } : {}),
					...(plan3d?.url ? { image3d: plan3d.url } : {}),
					...(svg?.url ? { blockSvg: svg.url } : {}),
				};

				if (editingBlock) {
					await updateBlockMutation.mutateAsync({ id: editingBlock.id, payload });
				} else {
					await createBlockMutation.mutateAsync({
						...payload,
						buildingId: building.id,
					});
				}
			} catch (error) {
				message.error(parseApiError(error) || "Blokni saqlashda xatolik yuz berdi");
			}
		});
	};

	return (
		<div className="crm-blocks">
			<div className="crm-page-head">
				<div>
					<nav className="crm-breadcrumb">
						<button
							type="button"
							className="crm-breadcrumb-item"
							onClick={() => navigate("/objects")}
						>
							Binolar
						</button>
						<span className="crm-breadcrumb-sep">/</span>
						<span className="crm-breadcrumb-current">{building.name}</span>
					</nav>
					<div className="crm-page-title-row">
						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							className="crm-back-btn"
							onClick={() => navigate("/objects")}
						/>
						<h1 style={{ margin: 0 }}>{building.name} — bloklar</h1>
					</div>
					<p style={{ color: "var(--crm-ink-3)", marginTop: "4px", marginLeft: "44px" }}>
						{buildingBlocks.length} ta blok
					</p>
				</div>
				{canManageBlocks ? (
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						onClick={() => setModalVisible(true)}
					>
						Yangi blok
					</Button>
				) : null}
			</div>

			<div className="col crm-block-list" style={{ gap: "16px" }}>
				{buildingBlocks.map((block) => (
					<div className="crm-card crm-block-card" key={block.id}>
						<div className="crm-card-body crm-block-card-body">
							<BlockPreview
								block={block}
								onDelete={
									canManageBlocks
										? (type) => {
												updateBlockMutation.mutate({
													id: block.id,
													payload: {
														[type]: "",
													},
												});
											}
										: undefined
								}
							/>

							<div className="grow" style={{ minWidth: "200px" }}>
								<div className="crm-block-title-line">
									<div className="crm-block-title-copy">
										<strong>{block.name}</strong>
										<span className="crm-status-badge green">{block.floorsCount} qavat</span>
									</div>
									{canManageBlocks ? (
										<div className="crm-block-inline-actions">
											<Button
												icon={<EditOutlined />}
												onClick={() => handleEditBlock(block)}
												aria-label="Blokni tahrirlash"
												type="text"
												size="small"
											/>
											<Popconfirm
												cancelText="Yo'q"
												okButtonProps={{
													danger: true,
													loading:
														deleteBlockMutation.isPending &&
														deleteBlockMutation.variables === block.id,
												}}
												okText="Ha"
												onConfirm={() => handleDeleteBlock(block)}
												title="Blokni o'chirish?"
											>
												<Button
													danger
													icon={<DeleteOutlined />}
													loading={
														deleteBlockMutation.isPending &&
														deleteBlockMutation.variables === block.id
													}
													aria-label="Blokni o'chirish"
													type="text"
													size="small"
												/>
											</Popconfirm>
										</div>
									) : null}
								</div>
							</div>

							<div className="col gap-s crm-block-primary-actions">
								{canManageBlocks ? (
									<Button
										className="crm-primary-btn"
										style={{ width: "100%" }}
										onClick={() => navigate(`/objects/${buildingId}/blocks/${block.id}/sections`)}
									>
										Sektsiyalar
									</Button>
								) : null}
								<Button
									style={{ width: "100%" }}
									onClick={() =>
										navigate(`/rooms?${roomsMapBaseParams}&blockId=${block.id}`, {
											state: { returnTo: `/objects/${buildingId}/blocks` },
										})
									}
								>
									Xonalar
								</Button>
							</div>
						</div>
					</div>
				))}

				{buildingBlocks.length === 0 && (
					<div className="crm-card pad empty">Ushbu binoda hali bloklar yaratilmagan.</div>
				)}
			</div>

			<Modal
				title={editingBlock ? "Blokni tahrirlash" : "Yangi blok"}
				className="crm-object-modal crm-block-modal"
				width={460}
				open={modalVisible}
				onOk={handleSaveBlock}
				onCancel={() => {
					resetCreateModal();
					setModalVisible(false);
				}}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{
					className: "crm-primary-btn",
					loading: createBlockMutation.isPending || updateBlockMutation.isPending,
				}}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={{ floorsCount: 9 }}
					style={{ marginTop: "16px" }}
				>
					<Form.Item
						name="name"
						label="Blok nomi"
						rules={[{ required: true, message: "Blok nomini kiriting!" }]}
					>
						<Input placeholder="Masalan: A Blok" />
					</Form.Item>

					<div className="crm-inline-fields">
						<Form.Item
							name="floorsCount"
							label="Qavatlar soni"
							rules={[{ required: true, message: "Qavatlar sonini kiriting!" }]}
						>
							<InputNumber min={1} style={{ width: "100%" }} />
						</Form.Item>
					</div>

					<div className="crm-inline-fields crm-inline-mobile">
						<BlockUploadField
							accept="image/png,image/jpeg,image/webp"
							existingAsset={editingBlock?.image}
							file={plan2dFile}
							label="2D rasm"
							onFileChange={setPlan2dFile}
						/>
						<BlockUploadField
							accept="image/png,image/jpeg,image/webp"
							existingAsset={editingBlock?.image3d}
							file={plan3dFile}
							label="3D rasm"
							onFileChange={setPlan3dFile}
						/>
						<BlockUploadField
							accept="image/svg+xml,.svg"
							existingAsset={editingBlock?.blockSvg}
							file={svgFile}
							label="SVG plan"
							onFileChange={setSvgFile}
						/>
					</div>
				</Form>
			</Modal>
		</div>
	);
}
